/**
 * BatchWorkspaceGallery - Modal for selecting workspace images
 *
 * Uses the same ImageGallery component as AspectRatioMode/ConvertMode/UpscaleMode
 */

import { useState, useCallback } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Loading, EmptyState } from '@/components/v2/ui';
import { ImageGallery } from '@/components/v2/tools/gallery';
import { useWorkspaceImages } from '@/hooks/useWorkspaceImages';
import type { WorkspaceImage } from '@/lib/api/batch';

interface BatchWorkspaceGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  selectedImages: WorkspaceImage[];
  onSelect: (images: WorkspaceImage[]) => void;
  maxSelect?: number;
}

export function BatchWorkspaceGallery({
  isOpen,
  onClose,
  selectedImages,
  onSelect,
  maxSelect = 10,
}: BatchWorkspaceGalleryProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [localSelected, setLocalSelected] = useState<Set<string>>(
    new Set(selectedImages.map((img) => img.id))
  );

  // Use the SAME hook as AspectRatioMode/ConvertMode/UpscaleMode
  const { images, pagination, isLoading, isFetching } = useWorkspaceImages({
    page: currentPage,
    perPage: 24,
    enabled: isOpen,
  });

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    // Enforce max selection
    if (ids.size <= maxSelect) {
      setLocalSelected(ids);
    }
  }, [maxSelect]);

  const handleConfirm = () => {
    // Convert Image[] to WorkspaceImage[]
    // Note: Image type has image_url and proxy_url, not thumbnail_url
    const selectedList = images?.filter((img) => localSelected.has(img.id)) || [];
    const result: WorkspaceImage[] = selectedList.map((img) => ({
      id: img.id,
      thumbnail_url: img.proxy_url || img.image_url || null,
      mime_type: 'image/png', // Image type doesn't have mime_type
      created_at: img.created_at,
    }));

    console.log('[Gallery Confirm] Selected images:', result.map(img => ({
      id: img.id,
      thumbnail_url: img.thumbnail_url ? 'HAS URL' : 'NULL',
      url_preview: img.thumbnail_url?.substring(0, 80)
    })));

    onSelect(result);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelected(new Set(selectedImages.map((img) => img.id)));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="relative w-full max-w-6xl max-h-[85vh] bg-white dark:bg-neutral-900 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t('batch.selectFromGallery')}
            </h2>
            <p className="text-sm text-neutral-500 mt-0.5">
              {t('batch.selectedCount', { current: localSelected.size, max: maxSelect })}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        {/* Content - Use ImageGallery component */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loading size="lg" />
            </div>
          ) : images && images.length > 0 ? (
            <ImageGallery
              images={images}
              isLoading={isLoading}
              isFetching={isFetching}
              pagination={pagination}
              onPageChange={setCurrentPage}
              selectedIds={localSelected}
              onSelectionChange={handleSelectionChange}
            />
          ) : (
            <EmptyState
              icon={<ImageIcon className="h-12 w-12" />}
              title={t('gallery.noImages')}
              description="Es sind noch keine Bilder in der Galerie vorhanden."
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 dark:border-neutral-700">
          <Button variant="outline" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={localSelected.size === 0}>
            {t('batch.adoptImages', { count: localSelected.size, suffix: localSelected.size === 1 ? t('common.image') : t('common.images') })}
          </Button>
        </div>
      </div>
    </div>
  );
}
